import { IPaginationInput } from '@models/payload/customer';
import { CreateFoodShiftPayload, UpdateFoodShiftPayload } from '@models/payload/foodShift';
import { IError, IErrorResponse } from '@models/response/error';
import { FoodShiftDetailQueryResponse, FoodShiftsResponse } from '@models/response/foodShift';
import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import { createFoodShiftService, getFoodShifts, getFoodShiftsId, updateFoodShift } from '@services/foodShift';
import { AxiosError } from 'axios';
import { createAppAsyncThunk } from '../utils/reduxUtil';

interface FoodShiftsStateInterface {
  pageSize: number;
  pageNumber: number;
  search: string;
  loading: boolean;
  shifts: FoodShiftsResponse | undefined;
  detail: FoodShiftDetailQueryResponse | undefined;
  errors: IError | undefined;
}

const initialState: FoodShiftsStateInterface = {
  loading: false,
  shifts: undefined,
  detail: undefined,
  pageNumber: 1,
  pageSize: 10,
  search: '',
  errors: undefined,
};

const getFoodShiftById = createAppAsyncThunk<FoodShiftDetailQueryResponse, string>(
  'foodShifts/getVendorFoodShiftById',
  async (id) => {
    const response = await getFoodShiftsId(id);
    return response;
  }
);

const getVendorFoodShifts = createAppAsyncThunk<FoodShiftsResponse, IPaginationInput>(
  'foodShifts/getVendorFoodShifts',
  async (input) => {
    const response = await getFoodShifts(input);
    return response;
  }
);

const createFoodShift = createAppAsyncThunk<void, CreateFoodShiftPayload>(
  'foodShifts/createVendorFoodShifts',
  async (input, { rejectWithValue }) => {
    try {
      const response = await createFoodShiftService(input);
      return response;
    } catch (err) {
      return rejectWithValue((err as AxiosError)?.response?.data);
    }
  }
);

const updateFoodShiftById = createAppAsyncThunk<void, UpdateFoodShiftPayload>(
  'foodShifts/updateVendorFoodShifts',
  async (input, { dispatch, getState }) => {
    const { pageNumber, pageSize, search } = getState().foodShifts;
    const response = await updateFoodShift(input);
    await dispatch(
      getVendorFoodShifts({
        PageNumber: pageNumber,
        PageSize: pageSize,
        Search: search,
      })
    );
    return response;
  }
);

const foodShiftsSlice = createSlice({
  name: 'foodShifts',
  initialState,
  reducers: {
    clearDetail: (state) => ({
      ...state,
      detail: undefined,
    }),
    setPageNumber: (state, action) => ({
      ...state,
      pageNumber: action.payload,
    }),
    setSearch: (state, action) => ({
      ...state,
      search: action.payload,
    }),
  },
  extraReducers: (builder: ActionReducerMapBuilder<FoodShiftsStateInterface>) => {
    builder.addCase(getVendorFoodShifts.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getVendorFoodShifts.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      shifts: action?.payload,
    }));

    builder.addCase(createFoodShift.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(createFoodShift.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(createFoodShift.rejected, (state, action) => ({
      ...state,
      loading: false,
      errors: (action?.payload as IErrorResponse).data!.errors as IError,
    }));

    builder.addCase(getFoodShiftById.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getFoodShiftById.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      detail: action.payload,
    }));

    builder.addCase(updateFoodShiftById.pending, (state) => ({
      ...state,
      loading: true,
    }));
  },
});

export default foodShiftsSlice;
export { createFoodShift, getFoodShiftById, getVendorFoodShifts, updateFoodShiftById };
